import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { spacing, statusPalette } from '@/theme/tokens';

import { uptimePctToString } from './format';

interface DayCell {
  /** Epoch ms — start of UTC day. */
  dayStartEpochMs: number;
  /** 0..100. */
  uptimePct: number;
}

interface UptimeHeatmapProps {
  data: readonly DayCell[];
  /** Number of weeks to show; older days are clipped. */
  weeks?: number;
  /** Show alternating-row weekday labels (Sun, Tue, Thu, Sat). */
  showWeekdayLabels?: boolean;
  /**
   * Anchor "today" to a specific epoch-ms. Defaults to `data`'s newest day,
   * keeping render pure (no `Date.now()` during render). Pass `Date.now()`
   * from the caller if you want a clock-relative grid.
   */
  todayEpochMs?: number;
}

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateRows: 'repeat(7, 1fr)',
    gap: '2px',
    alignItems: 'stretch',
  },
  cell: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
    transition: 'transform 80ms ease',
  },
  cellHover: {
    cursor: 'default',
    ':hover': { transform: 'scale(1.15)' },
  },
  layout: {
    display: 'flex',
    gap: spacing.s,
  },
  weekdayLabels: {
    display: 'grid',
    gridTemplateRows: 'repeat(7, 1fr)',
    gap: '2px',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    alignItems: 'center',
  },
  weekdayLabel: {
    height: '14px',
    lineHeight: '14px',
  },
  empty: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    paddingBlock: spacing.s,
  },
});

const DAYS_PER_WEEK = 7;
const DEFAULT_WEEKS = 5;
const HEALTHY_THRESHOLD = 99.9;
const DEGRADED_THRESHOLD = 99.0;

const NO_DATA_BG = 'var(--colorNeutralBackground3)';

function colorFor(pct: number | undefined): string {
  if (pct === undefined || !Number.isFinite(pct)) {
    return NO_DATA_BG;
  }
  if (pct >= HEALTHY_THRESHOLD) {
    return statusPalette.healthy;
  }
  if (pct >= DEGRADED_THRESHOLD) {
    return statusPalette.degraded;
  }
  return statusPalette.unhealthy;
}

/**
 * GitHub-style uptime calendar — one cell per day, columns are weeks.
 * Maps `data` onto a fixed `weeks × 7` grid; missing days render in the
 * neutral "no data" background.
 *
 * Accessibility: the wrapping grid carries `role="img"` with a single
 * descriptive `aria-label` (e.g. "Daily uptime over the last 5 weeks").
 * The per-cell `title` attribute provides hover tooltips for sighted
 * users, but it is NOT relied on for screen-reader announcements —
 * `title` support across screen readers is inconsistent, especially on
 * non-interactive elements like these `<span>`s.
 */
export function UptimeHeatmap({
  data,
  weeks = DEFAULT_WEEKS,
  showWeekdayLabels = true,
  todayEpochMs,
}: UptimeHeatmapProps) {
  const classes = useStyles();
  // Memoize the bucketed grid so changing an unrelated parent prop (e.g.
  // a sibling's chart range tab) doesn't rebuild the cell array on every
  // render of the resource detail panel. `null` means we don't have
  // enough information to anchor "today" — see the early return below.
  const ordered = useMemo(() => {
    const byDay = new Map<number, number>();
    let newestInData = 0;
    for (const d of data) {
      const day = startOfDayUtc(d.dayStartEpochMs);
      byDay.set(day, d.uptimePct);
      if (day > newestInData) {
        newestInData = day;
      }
    }

    // Anchor "today" without reading the wall clock during render: prefer
    // an explicit `todayEpochMs` prop, otherwise the newest day in `data`.
    // If neither is available we have no point of reference — fall back
    // to an empty-state render below rather than fabricate a 1970 grid.
    const anchor = todayEpochMs ?? (newestInData > 0 ? newestInData : null);
    if (anchor === null) {
      return null;
    }
    const today = startOfDayUtc(anchor);
    // Align the rightmost column to the week containing `today`: its first
    // row is the most recent Sunday at or before `today`. The grid is
    // therefore exactly `weeks × 7` cells with row 0 = Sunday, row 6 =
    // Saturday — which makes the Sun/Tue/Thu/Sat row labels truthful.
    // Days after `today` (later in this week) render as "no data".
    // `today` is guaranteed UTC-day-aligned by `startOfDayUtc()` above,
    // so `new Date(today).getUTCDay()` is a cheap weekday lookup.
    const todayDow = new Date(today).getUTCDay(); // 0=Sun … 6=Sat
    const lastColumnSunday = today - todayDow * MS_PER_DAY;
    const firstColumnSunday = lastColumnSunday - (weeks - 1) * DAYS_PER_WEEK * MS_PER_DAY;

    const cells: { dayMs: number; pct: number | undefined }[] = [];
    for (let col = 0; col < weeks; col++) {
      for (let row = 0; row < DAYS_PER_WEEK; row++) {
        const dayMs = firstColumnSunday + (col * DAYS_PER_WEEK + row) * MS_PER_DAY;
        // Future days (after today) and pre-data gaps both surface as
        // `undefined` → "no data" cells.
        const pct = dayMs <= today ? byDay.get(dayMs) : undefined;
        cells.push({ dayMs, pct });
      }
    }
    return cells;
  }, [data, weeks, todayEpochMs]);

  if (ordered === null) {
    return (
      <div
        className={classes.empty}
        role="img"
        aria-label="Daily uptime — no data yet"
      >
        No uptime data yet.
      </div>
    );
  }

  return (
    <div className={classes.layout}>
      {showWeekdayLabels ? (
        <div className={classes.weekdayLabels} aria-hidden="true">
          {['Sun', '', 'Tue', '', 'Thu', '', 'Sat'].map((label, i) => (
            <span key={i} className={classes.weekdayLabel}>
              {label}
            </span>
          ))}
        </div>
      ) : null}
      <div
        className={classes.grid}
        role="img"
        aria-label={`Daily uptime over the last ${String(weeks)} weeks`}
      >
        {ordered.map(({ dayMs, pct }) => (
          <span
            key={dayMs}
            className={`${classes.cell} ${classes.cellHover}`}
            style={{ backgroundColor: colorFor(pct) }}
            title={
              pct === undefined
                ? `${formatDate(dayMs)}: no data`
                : `${formatDate(dayMs)}: ${uptimePctToString(pct / 100)}`
            }
          />
        ))}
      </div>
    </div>
  );
}

const MS_PER_DAY = 86_400_000;

function startOfDayUtc(epochMs: number): number {
  const d = new Date(epochMs);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
}

function formatDate(epochMs: number): string {
  return new Date(epochMs).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
